/**
 * Sample Mapper Module for Pillovely
 *
 * Handles mapping of product fabric variants to sample products.
 *
 * @version 1.0.0
 */

const SampleMapper = (() => {
  'use strict';

  // =============================================================================
  // CONSTANTS & CONFIGURATION
  // =============================================================================

  const PATTERNS = Object.freeze({
    PARENTHESES: /^(.+?)\s*\(([^)]+)\)$/,
    WHITESPACE: /\s+/g,
    DIACRITICS: /[\u0300-\u036f]/g,
    NUMERIC: /^\d+$/
  });

  const CONFIG = Object.freeze({
    CACHE_TTL: 5 * 60 * 1000, // 5 minutes
    CACHE_KEY: 'pillovely_samples_cache',
    SAMPLE_TAG: 'probnik',
    SAMPLES_COLLECTION: 'material-samples',
    PRODUCTS_PER_COLLECTION: 250,
    DEBUG: false,
    MAX_NORMALIZATION_CACHE: 1000
  });

  const TRANSLATIONS = Object.freeze({
    fabric_types: {
      poso: ['poso', 'sztruks'],
      bella: ['bella'],
      casablanca: ['casablanca'],
      sztruks: ['sztruks', 'corduroy', 'cord'],
      fresh: ['fresh'],
      cream: ['cream', 'krem'],
      velvet: ['velvet', 'aksamit', 'welur'],
      linen: ['linen', 'len']
    },
    colors: {
      machon: ['mahogany', 'mahon'],
      mahon: ['mahogany', 'machon', 'mahon'],
      szampanski: ['champagne', 'szampan'],
      szary: ['gray', 'grey', 'szary'],
      'szampanski popiol': ['champagne ash', 'champagne gray'],
      'szary bambus': ['gray bamboo', 'grey bamboo', 'bamboo gray'],
      bialy: ['white', 'biel'],
      czarny: ['black', 'czern'],
      bezowy: ['beige', 'bez'],
      brazowy: ['brown', 'braz'],
      niebieski: ['blue', 'blekit'],
      zielony: ['green', 'zielen'],
      czerwony: ['red', 'czerwien'],
      zolty: ['yellow'],
      rozowy: ['pink', 'roz'],
      fioletowy: ['purple', 'violet', 'fiolet'],
      pomaranczowy: ['orange', 'pomarancz'],
      granatowy: ['navy', 'navy blue', 'granat'],
      turkusowy: ['turquoise', 'turkus'],
      oliwkowy: ['olive', 'oliwka'],
      bordowy: ['burgundy', 'bordo', 'maroon'],
      kremowy: ['cream', 'krem', 'ivory'],
      piaskowy: ['sand', 'sandy', 'piasek'],
      grafitowy: ['graphite', 'grafit', 'charcoal'],
      antracytowy: ['anthracite', 'antracyt'],
      musztardowy: ['mustard', 'musztarda'],
      butelkowy: ['bottle green', 'bottle'],
      ceglany: ['brick', 'cegla'],
      cappuccino: ['cappuccino', 'cappucino'],
      ecru: ['ecru', 'off-white'],
      mahogany: ['machon', 'mahon'],
      champagne: ['szampanski', 'szampan'],
      gray: ['szary'],
      grey: ['szary'],
      white: ['bialy', 'biel'],
      black: ['czarny', 'czern'],
      beige: ['bezowy', 'bez'],
      brown: ['brazowy', 'braz'],
      blue: ['niebieski', 'blekit'],
      green: ['zielony', 'zielen'],
      red: ['czerwony', 'czerwien'],
      yellow: ['zolty'],
      pink: ['rozowy', 'roz'],
      purple: ['fioletowy', 'fiolet'],
      orange: ['pomaranczowy', 'pomarancz'],
      navy: ['granatowy', 'granat']
    }
  });

  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================

  class StateManager {
    constructor() {
      this.samplesCache = null;
      this.cacheTimestamp = null;
      this.isLoading = false;
      this.loadPromise = null;
      this.samplesIndex = null;
      this.translationIndex = null;
      this.normalizationCache = new Map();
    }

    reset() {
      this.samplesCache = null;
      this.cacheTimestamp = null;
      this.isLoading = false;
      this.loadPromise = null;
      this.samplesIndex = null;
      this.translationIndex = null;
      this.normalizationCache.clear();
    }

    isCacheValid() {
      return this.samplesCache &&
        this.cacheTimestamp &&
        Date.now() - this.cacheTimestamp < CONFIG.CACHE_TTL;
    }
  }

  const state = new StateManager();

  // =============================================================================
  // LOGGING UTILITIES
  // =============================================================================

  const logger = {
    log: (...args) => CONFIG.DEBUG && console.log('[SampleMapper]', ...args),
    warn: (...args) => console.warn('[SampleMapper]', ...args)
  };

  // =============================================================================
  // STRING NORMALIZATION
  // =============================================================================

  const normalizeString = (() => {
    return (str) => {
      if (!str) return '';

      // Check cache first
      if (state.normalizationCache.has(str)) {
        return state.normalizationCache.get(str);
      }

      const normalized = str
        .toLowerCase()
        .normalize('NFD')
        .replace(PATTERNS.DIACRITICS, '')
        .replace(PATTERNS.WHITESPACE, ' ')
        .trim();

      // Limit cache size to prevent memory issues
      if (state.normalizationCache.size < CONFIG.MAX_NORMALIZATION_CACHE) {
        state.normalizationCache.set(str, normalized);
      }

      return normalized;
    };
  })();

  // =============================================================================
  // SIMILARITY CALCULATION
  // =============================================================================

  const calculateSimilarity = (str1, str2) => {
    const s1 = normalizeString(str1);
    const s2 = normalizeString(str2);

    if (s1 === s2) return 1;
    if (!s1 || !s2) return 0;
    if (s1.includes(s2) || s2.includes(s1)) return 0.9;

    const tokens1 = s1.split(/\s+/);
    const tokens2 = s2.split(/\s+/);

    const matchCount = tokens1.reduce((count, t1) => {
      return count + (tokens2.some(t2 =>
        t1 === t2 || t1.includes(t2) || t2.includes(t1)
      ) ? 1 : 0);
    }, 0);

    return matchCount / Math.max(tokens1.length, tokens2.length);
  };

  // =============================================================================
  // LOCALE DETECTION
  // =============================================================================

  const getCurrentLocale = () => {
    if (window.Shopify?.locale) return window.Shopify.locale;

    const lang = document.documentElement.lang;
    if (lang) return lang.substring(0, 2).toLowerCase();

    const path = window.location.pathname;
    if (path.startsWith('/en/') || path.startsWith('/en-')) return 'en';

    return 'pl';
  };

  // =============================================================================
  // TRANSLATION INDEX
  // =============================================================================

  const buildTranslationIndex = () => {
    if (state.translationIndex) return state.translationIndex;

    const index = {
      fabricTypes: new Map(),
      colors: new Map()
    };

    // Build fabric type index
    for (const [canonical, aliases] of Object.entries(TRANSLATIONS.fabric_types)) {
      const normalizedCanonical = normalizeString(canonical);
      index.fabricTypes.set(normalizedCanonical, canonical);

      aliases.forEach(alias => {
        index.fabricTypes.set(normalizeString(alias), canonical);
      });
    }

    // Build color index
    for (const [canonical, aliases] of Object.entries(TRANSLATIONS.colors)) {
      const normalizedCanonical = normalizeString(canonical);
      index.colors.set(normalizedCanonical, canonical);

      aliases.forEach(alias => {
        index.colors.set(normalizeString(alias), canonical);
      });
    }

    state.translationIndex = index;

    logger.log('Translation index built:', {
      fabricTypes: index.fabricTypes.size,
      colors: index.colors.size
    });

    return index;
  };

  // =============================================================================
  // CACHE OPERATIONS
  // =============================================================================

  const CacheManager = {
    load() {
      try {
        const cached = localStorage.getItem(CONFIG.CACHE_KEY);
        if (!cached) return null;

        const data = JSON.parse(cached);
        const isValid = data.timestamp &&
          Date.now() - data.timestamp < CONFIG.CACHE_TTL;

        if (isValid) {
          logger.log(`Loaded ${data.samples?.length || 0} samples from cache`);
          return data;
        }

        logger.log('Cache expired');
        return null;
      } catch (error) {
        logger.warn('Error loading cache:', error);
        return null;
      }
    },

    save(samples) {
      try {
        const data = {
          timestamp: Date.now(),
          samples
        };
        localStorage.setItem(CONFIG.CACHE_KEY, JSON.stringify(data));
        logger.log(`Saved ${samples.length} samples to cache`);
      } catch (error) {
        logger.warn('Error saving to cache:', error);
      }
    },

    invalidate() {
      try {
        localStorage.removeItem(CONFIG.CACHE_KEY);
        state.reset();
        logger.log('Cache invalidated');
      } catch (error) {
        logger.warn('Error invalidating cache:', error);
      }
    }
  };

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  const fetchCollectionProducts = async (collectionHandle) => {
    try {
      const allProducts = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const url = `/collections/${collectionHandle}/products.json?limit=${CONFIG.PRODUCTS_PER_COLLECTION}&page=${page}`;
        const response = await fetch(url);

        if (!response.ok) {
          if (response.status === 404) {
            logger.log(`Collection "${collectionHandle}" not found`);
            return allProducts;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const products = data.products || [];
        allProducts.push(...products);

        hasMore = products.length === CONFIG.PRODUCTS_PER_COLLECTION;
        page++;
      }

      logger.log(`Fetched ${allProducts.length} products from "${collectionHandle}" (${page - 1} page(s))`);
      return allProducts;
    } catch (error) {
      logger.warn(`Error fetching collection "${collectionHandle}":`, error);
      return [];
    }
  };

  const fetchAllSamples = async () => {
    logger.log('Fetching sample products from:', CONFIG.SAMPLES_COLLECTION);

    const products = await fetchCollectionProducts(CONFIG.SAMPLES_COLLECTION);
    const sampleTag = normalizeString(CONFIG.SAMPLE_TAG);

    const samples = products.filter(p =>
      p.tags?.some(tag => {
        const normalized = normalizeString(tag);
        return normalized.includes(sampleTag) || normalized === 'probnik';
      })
    );

    logger.log(`Fetched ${samples.length} sample products`);
    return samples;
  };

  // =============================================================================
  // SAMPLES INDEX
  // =============================================================================

  const buildSamplesIndex = (samples) => {
    const index = new Map();
    const translationIdx = buildTranslationIndex();

    for (const sample of samples) {
      const sampleTitle = normalizeString(sample.title);
      const sampleTags = (sample.tags || []).map(normalizeString);

      // Store normalized data
      sample._normalized = { title: sampleTitle, tags: sampleTags };

      // Index by fabric type from tags
      for (const tag of sampleTags) {
        const fabricType = translationIdx.fabricTypes.get(tag);
        if (fabricType) {
          if (!index.has(fabricType)) {
            index.set(fabricType, []);
          }
          index.get(fabricType).push(sample);
        }
      }

      // Index by fabric type from title
      for (const [normalizedType, fabricType] of translationIdx.fabricTypes) {
        if (sampleTitle.includes(normalizedType)) {
          if (!index.has(fabricType)) {
            index.set(fabricType, []);
          }
          const list = index.get(fabricType);
          if (!list.includes(sample)) {
            list.push(sample);
          }
        }
      }
    }

    logger.log(`Samples index built: ${index.size} fabric types`);
    return index;
  };

  // =============================================================================
  // SAMPLE LOADING
  // =============================================================================

  const loadSamples = async (forceRefresh = false) => {
    // Return cached samples if valid
    if (!forceRefresh && state.isCacheValid()) {
      return state.samplesCache;
    }

    // Try loading from localStorage
    if (!forceRefresh) {
      const cached = CacheManager.load();
      if (cached?.samples) {
        state.samplesCache = cached.samples;
        state.cacheTimestamp = cached.timestamp;
        state.samplesIndex = buildSamplesIndex(state.samplesCache);
        return state.samplesCache;
      }
    }

    // Return existing load promise if already loading
    if (state.isLoading && state.loadPromise) {
      return state.loadPromise;
    }

    // Fetch fresh data
    state.isLoading = true;
    state.loadPromise = (async () => {
      try {
        const samples = await fetchAllSamples();
        state.samplesCache = samples;
        state.cacheTimestamp = Date.now();
        CacheManager.save(samples);
        state.samplesIndex = buildSamplesIndex(samples);
        return samples;
      } finally {
        state.isLoading = false;
        state.loadPromise = null;
      }
    })();

    return state.loadPromise;
  };

  // =============================================================================
  // VARIANT PARSING
  // =============================================================================

  const parseParenthesesFormat = (original, match) => {
    const colorName = match[1].trim();
    const parenthesesContent = match[2].trim();
    const contentParts = parenthesesContent.split(/\s+/);
    const fabricTypeCandidate = contentParts[0];

    const translationIdx = buildTranslationIndex();
    const normalizedCandidate = normalizeString(fabricTypeCandidate);
    const fabricType = translationIdx.fabricTypes.get(normalizedCandidate) || normalizedCandidate;
    const colorVariant = normalizeString(colorName);
    // Store full content in parentheses for exact matching (e.g., "FRESH 01")
    const internalId = parenthesesContent;

    logger.log('Parsed (parentheses):', { original, fabricType, colorVariant, internalId });

    return { fabricType, colorVariant, original, internalId };
  };

  const parseSimpleFormat = (original) => {
    const normalized = normalizeString(original);
    const parts = normalized.split(/\s+/);
    const translationIdx = buildTranslationIndex();

    // Check if first part is fabric type
    let fabricType = translationIdx.fabricTypes.get(parts[0]);
    if (fabricType) {
      return {
        fabricType,
        colorVariant: parts.slice(1).join(' '),
        original,
        internalId: null
      };
    }

    // Search for fabric type in any position
    for (let i = 0; i < parts.length; i++) {
      fabricType = translationIdx.fabricTypes.get(parts[i]);
      if (fabricType) {
        const remaining = [...parts.slice(0, i), ...parts.slice(i + 1)];
        return {
          fabricType,
          colorVariant: remaining.join(' '),
          original,
          internalId: null
        };
      }
    }

    // Fallback: first part as fabric type
    logger.log('Parsed (simple):', {
      original,
      fabricType: parts[0] || '',
      colorVariant: parts.slice(1).join(' ')
    });

    return {
      fabricType: parts[0] || '',
      colorVariant: parts.slice(1).join(' ') || '',
      original,
      internalId: null
    };
  };

  const parseVariantOption = (optionValue) => {
    if (!optionValue) {
      return { fabricType: null, colorVariant: null, original: '', internalId: null };
    }

    const original = optionValue.trim();
    const parenthesesMatch = original.match(PATTERNS.PARENTHESES);

    return parenthesesMatch
      ? parseParenthesesFormat(original, parenthesesMatch)
      : parseSimpleFormat(original);
  };

  // =============================================================================
  // SAMPLE MATCHING
  // =============================================================================

  const calculateMatchScore = (sample, fabricType, colorVariant, internalId) => {
    // Simplified matching: only check if the content in parentheses matches
    // Extract content from sample title (e.g., "FRESH 01" from "Próbnik tkanin Fresh (FRESH 01)")
    const sampleTitle = sample.title || '';
    const sampleMatch = sampleTitle.match(PATTERNS.PARENTHESES);

    if (!sampleMatch || !internalId) {
      return 0;
    }

    // Compare full content in parentheses (e.g., "FRESH 01")
    const sampleId = normalizeString(sampleMatch[2]);
    const optionId = normalizeString(internalId);

    // Exact match on the full ID in parentheses
    return sampleId === optionId ? 1 : 0;
  };

  const findSampleInList = (samples, normalizedFabric, normalizedColor, internalId) => {
    let bestMatch = null;
    let bestScore = 0;

    for (const sample of samples) {
      const score = calculateMatchScore(sample, normalizedFabric, normalizedColor, internalId);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = sample;
      }
    }

    if (bestMatch && bestScore >= 0.5) {
      logger.log('Found match:', {
        title: bestMatch.title,
        score: bestScore,
        id: bestMatch.id
      });
      return bestMatch;
    }

    logger.warn('No matching sample found');
    return null;
  };

  const findSampleByFabricAndColor = async (fabricType, colorName, internalId = null, locale = 'pl') => {
    await loadSamples();

    if (!state.samplesIndex) {
      logger.warn('Samples index not available');
      return null;
    }

    const normalizedFabric = normalizeString(fabricType);
    const normalizedColor = normalizeString(colorName);

    logger.log('Searching:', { normalizedFabric, normalizedColor, internalId });

    const candidates = state.samplesIndex.get(fabricType) || [];

    if (candidates.length === 0) {
      logger.log('No indexed samples, searching all');
      return findSampleInList(state.samplesCache || [], normalizedFabric, normalizedColor, internalId);
    }

    logger.log(`Searching ${candidates.length} indexed samples for: ${fabricType}`);
    return findSampleInList(candidates, normalizedFabric, normalizedColor, internalId);
  };

  const getSampleForVariant = async (variantOption2, locale = 'pl') => {
    const samples = await loadSamples();
    if (!samples?.length) {
      logger.warn('No samples available');
      return null;
    }

    const parsed = parseVariantOption(variantOption2);
    logger.log('Finding sample for:', { variantOption2, parsed, locale });

    if (!parsed.fabricType) {
      logger.warn('Could not parse fabric type from:', variantOption2);
      return null;
    }

    return findSampleByFabricAndColor(parsed.fabricType, parsed.colorVariant, parsed.internalId, locale);
  };

  // =============================================================================
  // CART OPERATIONS
  // =============================================================================

  /**
   * Notifies the theme's cart-count / cart-drawer components and refreshes
   * the drawer contents. themePreLive drives these via `theme.pubsub`
   * (cartUpdate) and a `cart:refresh` document event handled by the
   * <cart-drawer> custom element (see assets/cart.js), rather than the
   * `cart:updated` document event / global `publish()` used in themeLive.
   */
  const refreshCart = async (source) => {
    try {
      const cart = await (await fetch('/cart.js')).json();

      if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source, cart });
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    } catch (error) {
      logger.log('Could not refresh cart:', error);
    }
  };

  const addSampleToCart = async (sampleVariantId, quantity = 1) => {
    logger.log('Adding to cart:', { sampleVariantId, quantity });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: sampleVariantId,
          quantity
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add to cart: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      logger.log('Successfully added to cart:', data);

      await refreshCart('sample-button');

      return data;
    } catch (error) {
      logger.warn('Error adding to cart:', error);
      throw error;
    }
  };

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  return Object.freeze({
    getSampleForVariant,
    findSampleByFabricAndColor,
    addSampleToCart,
    invalidateCache: CacheManager.invalidate,
    loadSamples,
    parseVariantOption,
    getCurrentLocale,
    normalizeString,
    get config() {
      return { ...CONFIG };
    }
  });
})();

window.SampleMapper = SampleMapper;
